/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { Dispatch, StateUpdater } from 'preact/hooks';
import type { ReorderDetail } from '#UNSAFE_Collection';

import { useMemo, useState } from 'preact/hooks';
import { useReorderable } from '#hooks/PRIVATE_useReorderable';
import { dragToScroll } from '#utils/PRIVATE_collectionUtils';

type useListViewReorderProps<K> = {
  rootRef: RefObject<HTMLDivElement>;
  scrollerRef: RefObject<HTMLElement>;
  itemSelector: string;
  onReorder?: (detail: ReorderDetail<K>) => void;
};

export type ReorderItemProps<K> = {
  rootRef: RefObject<HTMLDivElement>;
  dragKey?: K;
  setDragKey: Dispatch<StateUpdater<K | undefined>>;
  onReorder?: (detail: ReorderDetail<K>) => void;
};

export const useItemReorder = <K extends string | number>({
  rootRef,
  scrollerRef,
  onReorder,
  itemSelector
}: useListViewReorderProps<K>) => {
  const isReorderEnabled = onReorder != undefined;

  const [dragKey, setDragKey] = useState<K | undefined>(undefined);
  const { reorderProps, reorderContext, reorderInstructionsId, reorderInstructions } =
    useReorderable({
      onReorder,
      rootRef,
      itemSelector,
      isDisabled: !isReorderEnabled,
      columns: 1
    });

  const reorderItemProps: ReorderItemProps<K> = useMemo(() => {
    return { dragKey, setDragKey, onReorder, rootRef };
  }, [dragKey, onReorder, rootRef]);

  if (!isReorderEnabled) {
    return {
      reorderProps: {},
      reorderKeyboardProps: reorderProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } as const;
  }

  const handleDragOver = (event: DragEvent) => {
    if (dragKey !== undefined) {
      dragToScroll(event, dragKey !== undefined, scrollerRef.current);
    }
  };

  return {
    reorderProps: { onDragOver: handleDragOver },
    reorderKeyboardProps: reorderProps,
    reorderItemProps,
    reorderContext,
    reorderInstructionsId,
    reorderInstructions
  } as const;
};
